import { getGroupAuthType } from "@/utils/authToken";
import { httpAuth } from "@/api/httpAuth";
import { httpPublic } from "@/api/httpPublic";

import { GROUP_AUTH_HEADER_NAME } from "../constants";

export const patientRequests = {
  add_to_wait_list: function ({ data, searchParams }) {
    return httpAuth
      .post("/v1/patient/wait_list", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        searchParams: { method: "add", patient_username: searchParams.patient_username },
      })
      .json();
  },

  assign_assessment: function ({ data } = {}) {
    return httpAuth.post("/v1/patient/assessment/assign_assessment", { json: data }).json();
  },

  /**
   * Assessment related api calls
   */
  fetch_assessment_status: function ({ context }) {
    return httpAuth.post("/v1/patient/dashboard", { context }).json();
  },

  fetch_evaluator_survey_question: function () {
    return httpAuth.post("/v1/survey/evaluator_question").json();
  },

  fetch_GAD7_questions: function () {
    return httpAuth.post("/v2/screening/gad_question").json();
  },

  fetch_insurance_eligibility_v2: function ({ context, data } = {}) {
    return httpPublic
      .post("/v1/insurance_eligibility/check_eligibility", { context, json: data })
      .json();
  },

  // intentionally unauthenticated — used during signup before a session exists;
  // get_insurance_list in patientTyped.ts uses httpAuth for the same endpoint post-login
  fetch_insurance_list: function ({ context } = {}) {
    return httpPublic.post("/v2/patient/insurance", { context }).json();
  },

  fetch_next_outcome_question: function ({ context, data } = {}) {
    return httpAuth.post("/v1/patient/outcome-assessment/next", { context, json: data }).json();
  },

  fetch_PHQ9_questions: function () {
    return httpAuth.post("/v2/screening/phq_question").json();
  },

  fetch_prescriber_survey_question: function () {
    return httpAuth.post("/v1/survey/prescriber_question").json();
  },

  /**
   * Schedule related api calls
   */

  fetch_previous_outcome_question: function () {
    return httpAuth.post("/v1/patient/outcome-assessment/previous").json();
  },

  fetch_profile: function ({ context }) {
    return httpAuth.post("/v1/patient/profile", { context }).json();
  },

  get_additional_days_for_patient_appointments: function ({ context, data } = {}) {
    return httpPublic
      .post("/v1/referral/get_additional_days_for_patient_appointments", { context, json: data })
      .json();
  },

  get_all_past_intake_appointments: function ({ context, data } = {}) {
    return httpAuth
      .post("/v1/referral/get_all_past_intake_appointments", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  get_cancellation_policy: function ({ data }) {
    return httpAuth.post("/v1/patient/get_cpt_code_cancellation_policy", { json: data }).json();
  },

  get_has_patient_competed_first_session_for_type: function ({ data }) {
    return httpAuth
      .post("/v1/patient/get_has_patient_competed_first_session_for_type", { json: data })
      .json();
  },

  get_last_question: function ({ context, data, headers } = {}) {
    return httpAuth
      .post("/v1/patient/assessment/last_question", { context, headers, json: data })
      .json();
  },

  get_next_question: function ({ context, data, headers } = {}) {
    return httpAuth
      .post("/v1/patient/assessment/get_question", { context, headers, json: data })
      .json();
  },

  get_patient_meta_data: function ({ context, headers } = {}) {
    return httpAuth.post("/v1/patient/patient_metadata", { context, headers }).json();
  },

  get_provider_meta_data: function ({ context } = {}) {
    return httpAuth
      .post("/v1/patient/provider_metadata", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  get_upcoming_intake_appointments: function ({ context, data } = {}) {
    return httpAuth
      .post("/v1/referral/get_upcoming_intake_appointments", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  get_upcoming_intake_availability: function ({ context, data } = {}) {
    return httpPublic
      .post("/v1/referral/get_upcoming_intake_availability", { context, json: data })
      .json();
  },

  remove_from_wait_list: function ({ searchParams }) {
    return httpAuth
      .post("/v1/patient/wait_list", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { method: "remove", patient_username: searchParams.patient_username },
      })
      .json();
  },

  report_acuity: function ({ data }) {
    return httpAuth.post("/v1/patient/report_acuity", { json: data }).json();
  },

  send_evaluator_survey_answer: function ({ data }) {
    return httpAuth.post("/v1/survey/evaluator_survey", { json: data }).json();
  },

  send_nps_survey_answers: function ({ data }) {
    return httpAuth.post("/v1/survey/nps_survey", { json: data }).json();
  },

  send_prescriber_survey_answer: function ({ data }) {
    return httpAuth.post("/v1/survey/prescriber_survey", { json: data }).json();
  },

  start_outcome_assessment: function () {
    return httpAuth.post("/v1/patient/outcome-assessment/start").json();
  },

  submit_GAD7_questions: function ({ data }) {
    return httpAuth
      .post("/v2/screening/gad_screening", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  submit_next_question: function ({ context, data, headers } = {}) {
    return httpAuth
      .post("/v1/patient/assessment/submit_question", { context, headers, json: data })
      .json();
  },

  submit_PHQ9_questions: function ({ data }) {
    return httpAuth
      .post("/v2/screening/phq_screening", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  submit_question: function ({ context, data } = {}) {
    return httpAuth.post("/v1/patient/assessment/submit_question", { context, json: data }).json();
  },

  submit_rating_scale_questions: function ({ data }) {
    return httpAuth.post("/v1/patient/assessment/post_rating_scale", { json: data }).json();
  },

  update_email_preferences: function (data) {
    return httpAuth.post("/v1/patient/update_email_preferences", { json: data }).json();
  },

  update_preferences: function (data) {
    return httpAuth.post("/v1/patient/update_preferences", { json: data }).json();
  },

  update_profile: function ({ data }) {
    return httpAuth.post("/v1/patient/update_profile", { json: data }).json();
  },
};
